import { useEffect, useState } from "react";
import api, { formatApiError, formatINR } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Package, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Lot-picker modal used by Sales / Debit Note voucher lines when the
 * company's inventory_valuation_method === "SPECIFIC_IDENTIFICATION".
 *
 * Props:
 *   • open, onClose
 *   • companyId, itemId, itemName, unit
 *   • lineQty     — the qty on the parent stock-entry row (source of truth)
 *   • date        — voucher date; used as as_of for lot availability
 *   • excludeVoucherId — when editing, exclude this voucher from replay
 *   • initialSelections — [{lot_id, qty}] the parent already has
 *   • onSave(selections)  — called with the chosen lots
 */
export default function LotPickerModal({
  open, onClose,
  companyId, itemId, itemName, unit,
  lineQty, date, excludeVoucherId,
  initialSelections = [],
  onSave,
}) {
  const [lots, setLots] = useState([]);
  const [picks, setPicks] = useState({});  // lot_id -> qty
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !companyId || !itemId) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (date) params.set("as_of", date);
    if (excludeVoucherId) params.set("exclude_voucher_id", excludeVoucherId);
    api.get(`/companies/${companyId}/items/${itemId}/open-lots?${params.toString()}`)
      .then(({ data }) => {
        setLots(data.lots || []);
        // Seed picks with existing selections.
        const seed = {};
        (initialSelections || []).forEach((s) => { seed[s.lot_id] = Number(s.qty) || 0; });
        setPicks(seed);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
     
  }, [open, companyId, itemId]);

  const setPickQty = (lotId, val, maxQty) => {
    const n = Math.max(0, Math.min(Number(val) || 0, maxQty));
    setPicks((p) => ({ ...p, [lotId]: n }));
  };

  const totalPicked = Object.values(picks).reduce((s, q) => s + (Number(q) || 0), 0);
  const target = Number(lineQty) || 0;
  const remaining = +(target - totalPicked).toFixed(3);
  const balanced = Math.abs(remaining) < 1e-3;

  const autoFill = (mode) => {
    // "fifo" — fill oldest lots first
    // "lifo" — fill newest lots first
    const list = mode === "lifo" ? [...lots].reverse() : lots;
    let need = target;
    const next = {};
    for (const lot of list) {
      if (need <= 1e-6) break;
      const take = Math.min(lot.qty, need);
      if (take > 0) {
        next[lot.lot_id] = take;
        need -= take;
      }
    }
    setPicks(next);
  };

  const save = () => {
    if (!balanced) {
      toast.error(`Picked ${totalPicked} but line needs ${target}. Adjust by ${remaining > 0 ? `+${remaining}` : remaining}.`);
      return;
    }
    const selections = Object.entries(picks)
      .filter(([, q]) => Number(q) > 1e-6)
      .map(([lot_id, q]) => ({ lot_id, qty: Number(q) }));
    onSave(selections);
    onClose();
  };

  const totalAvailable = lots.reduce((s, l) => s + Number(l.qty || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="lot-picker-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-gray-100">
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-700" />
            Pick purchase lots — {itemName || "item"}
          </DialogTitle>
          <div className="text-xs text-gray-600 mt-1">
            Specific Identification method: choose which purchase lot(s) you are selling from.
            Selected qty must equal the line qty <b>{target} {unit || ""}</b>.
          </div>
        </DialogHeader>

        <div className="px-6 py-4 flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center text-sm text-gray-500 py-10">Loading available lots…</div>
          ) : lots.length === 0 ? (
            <div className="border border-amber-300 bg-amber-50 rounded p-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                <div className="font-semibold mb-1">No open purchase lots</div>
                This item has no available purchase lots on or before {date}. Post a
                Purchase voucher first, or select a different item.
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-gray-600">
                  {lots.length} open lot(s) · total available <b>{totalAvailable.toFixed(3)} {unit}</b>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => autoFill("fifo")}
                    className="text-xs text-blue-700 hover:underline"
                    data-testid="btn-lot-auto-fifo"
                  >
                    Auto-fill FIFO
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => autoFill("lifo")}
                    className="text-xs text-blue-700 hover:underline"
                    data-testid="btn-lot-auto-lifo"
                  >
                    Auto-fill LIFO
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => setPicks({})}
                    className="text-xs text-gray-600 hover:underline"
                    data-testid="btn-lot-clear"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="border border-gray-200 rounded overflow-hidden">
                <table className="tx-table">
                  <thead>
                    <tr>
                      <th style={{ width: "18%" }}>Date</th>
                      <th style={{ width: "18%" }}>Voucher No</th>
                      <th className="text-right" style={{ width: "14%" }}>Available</th>
                      <th className="text-right" style={{ width: "14%" }}>Rate</th>
                      <th className="text-right" style={{ width: "14%" }}>Lot value</th>
                      <th className="text-right" style={{ width: "22%" }}>Pick qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lots.map((lot) => {
                      const picked = Number(picks[lot.lot_id] || 0);
                      return (
                        <tr key={lot.lot_id} data-testid={`lot-row-${lot.lot_id}`}>
                          <td className="font-mono text-xs">{lot.date || "—"}</td>
                          <td className="font-mono text-xs">{lot.voucher_no || (lot.lot_id.startsWith("opening") ? "OPENING" : "—")}</td>
                          <td className="text-right font-mono">{Number(lot.qty).toFixed(3)}</td>
                          <td className="text-right font-mono">₹{Number(lot.rate).toFixed(2)}</td>
                          <td className="text-right font-mono text-gray-500">{formatINR(lot.value)}</td>
                          <td className="text-right">
                            <Input
                              type="number"
                              step="0.001"
                              min={0}
                              max={lot.qty}
                              value={picked || ""}
                              placeholder="0"
                              onChange={(e) => setPickQty(lot.lot_id, e.target.value, lot.qty)}
                              className="text-right font-mono w-24 ml-auto"
                              data-testid={`input-pick-${lot.lot_id}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div
          className={`px-6 py-3 border-t border-gray-100 flex items-center justify-between text-sm ${
            balanced ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"
          }`}
        >
          <div className="font-mono">
            Picked: <b>{totalPicked.toFixed(3)}</b> / {target.toFixed(3)} {unit}
            {!balanced && (
              <span className="ml-2 text-amber-800">
                (remaining: {remaining > 0 ? `+${remaining.toFixed(3)}` : remaining.toFixed(3)})
              </span>
            )}
          </div>
          <div className="text-xs">
            COGS at these picks:{" "}
            <b>
              {formatINR(
                Object.entries(picks).reduce((s, [lotId, q]) => {
                  const lot = lots.find((l) => l.lot_id === lotId);
                  return s + (lot ? Number(q) * Number(lot.rate) : 0);
                }, 0)
              )}
            </b>
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-gray-100 bg-white">
          <Button variant="outline" onClick={onClose} data-testid="btn-lot-cancel">Cancel</Button>
          <Button
            onClick={save}
            disabled={!balanced || lots.length === 0}
            className="bg-blue-700 hover:bg-blue-800"
            data-testid="btn-lot-save"
          >
            Confirm lots
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
